import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { environment } from "src/environments/environment";
import {
  MasqueradeUserResponse,
  MasqueradeStartResponse,
  MasqueradeCompanyInfo,
} from "src/app/core/models/masquerade.model";
import { ExtensionService } from "./extension.service";
import { SharedDataService } from "./shared-data.service";

/**
 * MasqueradeService - Centralized masquerade logic
 *
 * IMPORTANT DESIGN PRINCIPLE:
 * The masquerade tab is a pure sessionStorage bubble. It NEVER reads from
 * or writes to localStorage. The admin's tokens live in localStorage and
 * remain untouched throughout the masquerade session. This means:
 *
 *   - Other tabs (reading localStorage) stay signed in as the admin.
 *   - Closing the masquerade tab simply wipes its sessionStorage — no
 *     restore logic is needed because localStorage was never modified.
 *   - The DynamicStorage decorator routes reads/writes to sessionStorage
 *     when isMasquerading() is true, so feature services automatically
 *     isolate masqueraded data from admin data.
 */
@Injectable({
  providedIn: "root",
})
export class MasqueradeService {
  // Storage keys for masquerade data (all stored in sessionStorage)
  private readonly MASQUERADE_KEYS = {
    USER_ID: "fms-masquerade-user-id",
    USER_DETAILS: "fms-masquerade-user-details",
    COMPANY_ID: "fms-masquerade-company-id",
    COMPANY_SLUG: "fms-masquerade-company-slug",
    COMPANY_NAME: "fms-masquerade-company-name",
    START_TIME: "fms-masquerade-start-time",
    EXPIRES_AT: "fms-masquerade-expires-at",
    REAL_HEAP_ID: "fms-masquerade-real-heap-id",
  };

  private readonly ORIGINAL_FAVICON_KEY = "fms-masquerade-original-favicon";

  constructor(
    private readonly http: HttpClient,
    private readonly extensionService: ExtensionService,
    private readonly sharedData: SharedDataService
  ) {}

  /**
   * Start masquerade session from JWT token
   *
   * Flow:
   * 1. Call backend to decode token and get masquerade data
   * 2. Set masquerade user data in sessionStorage ONLY
   * 3. Sync extension
   * 4. Swap favicon
   *
   * localStorage is NEVER touched.
   */
  startMasqueradeFromToken(token: string): Observable<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .post<MasqueradeStartResponse>(
        `${environment.API_URL}v2/admin/masquerade/start`,
        { token },
        { headers }
      )
      .pipe(
        tap((response) => {
          const { result } = response;
          const user = result.user;

          // All writes go to sessionStorage ONLY — localStorage stays untouched
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.USER_ID,
            user.user_details.id
          );
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.USER_DETAILS,
            JSON.stringify(user)
          );
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.COMPANY_ID,
            user.user_details.company_id
          );
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.COMPANY_SLUG,
            user.user_details.company?.slug
          );
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.COMPANY_NAME,
            user.user_details.company?.name
          );
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.START_TIME,
            Date.now().toString()
          );
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.EXPIRES_AT,
            result.expires_at.toString()
          );

          // Heap: reset identity on masquerade entry so the impersonated user's
          // actions are never attributed to the real operator in analytics.
          // Persist the operator's heap id now — sharedData will be overwritten
          // with impersonated-user details once the session loads, so endMasquerade
          // must read from sessionStorage rather than sharedData.
          const realUserDetails = this.sharedData.getAttribute("userDetails");
          const realCompanyId = realUserDetails?.user_details?.company_id ?? "";
          sessionStorage.setItem(
            this.MASQUERADE_KEYS.REAL_HEAP_ID,
            realUserDetails?.user_details?.heap_analytics_id ?? ""
          );
          if (typeof window !== "undefined" && (window as any).heap) {
            (window as any).heap.resetIdentity();
            if (realCompanyId) {
              (window as any).heap.addUserProperties({
                masquerading: true,
                real_operator_company_id: realCompanyId,
              });
            }
          }

          // Swap favicon to masquerade icon
          this.setMasqueradeFavicon();

          // Sync extension. The .catch is required: a rejected promise inside
          // tap() is NOT caught by the stream's catchError, and masquerading
          // without the extension installed is a normal state.
          this.extensionService
            .sendExtensionMessage({
              request: "startMasquerade",
              data: {
                user: user.user_details,
                company: user.user_details.company,
              },
            })
            .catch(() => {});
        }),
        map(() => void 0),
        catchError((error) => {
          console.error("Failed to start masquerade:", error);
          return throwError(() => error);
        })
      );
  }

  /**
   * End masquerade session explicitly (via "Exit Masquerade" button).
   *
   * Flow:
   * 1. Clear ALL masquerade data from sessionStorage
   * 2. Restore original favicon
   * 3. Sync extension
   * 4. Return CMC URL for redirect
   *
   * localStorage is NEVER touched — admin tokens are already there.
   */
  endMasquerade(): Observable<string> {
    try {
      // Read the operator's Heap id from sessionStorage (written at entry).
      // sharedData is not safe here — auth.service overwrites it with the
      // impersonated user's details during the session.
      const realHeapId: string =
        sessionStorage.getItem(this.MASQUERADE_KEYS.REAL_HEAP_ID) ?? "";

      // 1. Clear ALL masquerade data from sessionStorage
      Object.values(this.MASQUERADE_KEYS).forEach((key) => {
        sessionStorage.removeItem(key);
      });
      sessionStorage.removeItem("masquerade-warning-shown");

      // Re-identify Heap with the real operator so subsequent events are
      // attributed correctly once the masquerade bubble is gone.
      if (typeof window !== "undefined" && (window as any).heap && realHeapId) {
        (window as any).heap.identify(realHeapId);
      }

      // 2. Restore original favicon
      this.restoreOriginalFavicon();

      // 3. Sync extension (.catch: the surrounding try/catch cannot catch an
      // async rejection, and no extension installed is a normal state)
      this.extensionService
        .sendExtensionMessage({
          request: "endMasquerade",
          data: {},
        })
        .catch(() => {});

      // 4. Return CMC URL for redirect
      return of(environment.CMC_APP_URL || "https://cmc.flymsg.io");
    } catch (error) {
      console.error("Failed to end masquerade:", error);
      return throwError(() => error);
    }
  }

  /**
   * Check if currently in masquerade session
   * Single source of truth for masquerade state
   */
  isMasquerading(): boolean {
    const userId = sessionStorage.getItem(this.MASQUERADE_KEYS.USER_ID);
    return !!userId && userId !== "null" && userId !== "undefined";
  }

  /**
   * Check if masquerade session is expired
   */
  isMasqueradeExpired(): boolean {
    if (!this.isMasquerading()) {
      return false;
    }

    const expiresAt = sessionStorage.getItem(this.MASQUERADE_KEYS.EXPIRES_AT);
    if (!expiresAt || expiresAt === "null" || expiresAt === "undefined") {
      return true; // No expiration data = expired
    }

    const expirationTime = parseInt(expiresAt, 10);
    return Date.now() > expirationTime * 1000; // Convert seconds to milliseconds
  }

  /**
   * Get masqueraded user details
   */
  getMasqueradeUser(): MasqueradeUserResponse | null {
    if (!this.isMasquerading()) {
      return null;
    }

    const userDetailsJson = sessionStorage.getItem(
      this.MASQUERADE_KEYS.USER_DETAILS
    );
    if (
      !userDetailsJson ||
      userDetailsJson === "null" ||
      userDetailsJson === "undefined"
    ) {
      return null;
    }

    return JSON.parse(userDetailsJson);
  }

  /**
   * Get masquerade company info
   */
  getMasqueradeCompanyInfo(): MasqueradeCompanyInfo {
    if (!this.isMasquerading()) {
      return { id: "", slug: "", name: "" };
    }

    const id = sessionStorage.getItem(this.MASQUERADE_KEYS.COMPANY_ID);
    const slug = sessionStorage.getItem(this.MASQUERADE_KEYS.COMPANY_SLUG);
    const name = sessionStorage.getItem(this.MASQUERADE_KEYS.COMPANY_NAME);

    return {
      id: id && id !== "null" && id !== "undefined" ? id : "",
      slug: slug && slug !== "null" && slug !== "undefined" ? slug : "",
      name: name && name !== "null" && name !== "undefined" ? name : "",
    };
  }

  /**
   * Get remaining time in minutes until masquerade expires
   */
  getRemainingTime(): number {
    if (!this.isMasquerading()) {
      return 0;
    }

    const expiresAt = sessionStorage.getItem(this.MASQUERADE_KEYS.EXPIRES_AT);
    if (!expiresAt || expiresAt === "null" || expiresAt === "undefined") {
      return 0;
    }

    const expirationTime = parseInt(expiresAt, 10) * 1000; // Convert to milliseconds
    const now = Date.now();
    const remaining = expirationTime - now;

    if (remaining <= 0) {
      return 0;
    }

    return Math.floor(remaining / (60 * 1000)); // Convert to minutes
  }

  // ------------------------------------------------------------------
  //  Favicon helpers
  // ------------------------------------------------------------------

  /**
   * Swap browser favicon to the masquerade icon so the user can visually
   * distinguish masquerade tabs from regular admin tabs.
   */
  setMasqueradeFavicon(): void {
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") ||
      document.createElement("link");

    // Remember the original favicon so we can restore it later
    if (!sessionStorage.getItem(this.ORIGINAL_FAVICON_KEY)) {
      sessionStorage.setItem(this.ORIGINAL_FAVICON_KEY, link.href || "");
    }

    link.type = "image/svg+xml";
    link.rel = "icon";
    link.href = "assets/images/masquerade.svg";

    // Ensure the <link> is in the <head>
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(link);
    }
  }

  /**
   * Restore the original favicon (called on endMasquerade).
   */
  restoreOriginalFavicon(): void {
    const original = sessionStorage.getItem(this.ORIGINAL_FAVICON_KEY);
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']");

    if (link && original) {
      link.href = original;
      link.type = "image/x-icon";
    }

    sessionStorage.removeItem(this.ORIGINAL_FAVICON_KEY);
  }
}
